import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Home, Loader2 } from "lucide-react";

function GoogleButton({ label }) {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" onClick={onClick} data-testid="google-login-btn" className="w-full h-11 rounded-xl gap-2">
      <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      {label}
    </Button>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(u.role === "agent" ? "/agent" : "/app/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1772463932558-6c72accdf73a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Hawaii home" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-heading text-3xl font-bold">Welcome back to Look Before You Leap 808</h2>
          <p className="mt-2 text-white/85">Your private home-buying workspace — an exclusive resource for Marlene's clients.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Home className="h-5 w-5 text-primary-foreground" /></div>
            <span className="font-heading text-xl font-bold">Look Before You Leap 808</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Log in</h1>
          <p className="text-muted-foreground mt-1 text-sm">Private access for clients of Marlene Alesna.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" placeholder="you@email.com" className="h-11 rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" placeholder="••••••••" className="h-11 rounded-xl" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full h-11 rounded-xl">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Log in"}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">or</span><div className="h-px bg-border flex-1" />
          </div>
          <GoogleButton label="Continue with Google" />
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Access is by invitation. Clients, use the private link Marlene sent you.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export { GoogleButton };
